import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Loader2, Plus, TrendingUp, Lightbulb, Eye, Pause, Play, PartyPopper, X, Check } from 'lucide-react';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Avatar, Help, fmtDate, rise, fireConfetti, SKILL_STYLE, SKILL_LABEL, TREND_STYLE, TREND_LABEL } from './shared';

function Dialog({ children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" data-testid={testid} onClick={onClose}>
      <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full td-pop max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function CreateGoalDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({ title: '', description: '', origin_note: '', skill_type: '' });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.title.trim()) return toast('Was willst du können? Ein Titel genügt für den Anfang.');
    if (!form.skill_type) return toast('Magst du noch wählen: Hard Skill oder Soft Skill?');
    setSaving(true);
    try {
      await api.teamDevCreateGoal(form);
      toast.success('Dein Ziel steht — das Team kann dich jetzt anfeuern. 🌱', { duration: 5000 });
      onCreated(); onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Anlegen hat nicht geklappt');
    } finally { setSaving(false); }
  };

  return (
    <Dialog onClose={onClose} testid="td-goal-create-dialog">
      <p className="text-sm font-medium text-foreground">Was willst du können? Wie willst du wirken?</p>
      <Help>Formuliere den Zielzustand, nicht das Problem. ✅ ‚Modelle verständlich und selbsterklärend aufbereiten‘ — ❌ ‚Unübersichtliche Modelle abstellen‘. Die Karte zeigt deine Ambition, nicht deine Schwäche.</Help>
      <div className="space-y-3.5 mt-4">
        <Input value={form.title} onChange={set('title')} placeholder="Dein Ziel in einem Satz …"
          className="text-sm bg-background rounded-md" data-testid="td-goal-title" />
        <Textarea value={form.description} onChange={set('description')} rows={2}
          placeholder="Optional: Woran merkst du, dass du angekommen bist?"
          className="text-sm bg-background rounded-md" data-testid="td-goal-description" />
        <div>
          <Textarea value={form.origin_note} onChange={set('origin_note')} rows={2}
            placeholder="Woher kommt dieses Ziel? (optional, nur für dich sichtbar)"
            className="text-sm bg-background rounded-md" data-testid="td-goal-origin" />
          <Help>Diese Notiz sieht wirklich nur du — auch keine Admins.</Help>
        </div>
        <div className="flex gap-2">
          {['hard', 'soft'].map((s) => (
            <button key={s} onClick={() => setForm((f) => ({ ...f, skill_type: s }))} data-testid={`td-goal-skill-${s}`}
              className={`flex-1 text-xs rounded-lg border px-3 py-2.5 transition-colors ${form.skill_type === s ? SKILL_STYLE[s] : 'border-border text-muted-foreground hover:text-foreground'}`}>
              {form.skill_type === s && <Check className="w-3 h-3 inline mr-1" />}
              {s === 'hard' ? 'Hard Skill (Fachliches)' : 'Soft Skill (Wirkung & Zusammenarbeit)'}
            </button>
          ))}
        </div>
        <div className="flex gap-2 pt-1">
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full flex-1" data-testid="td-goal-create-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />} Ziel anlegen
          </Button>
          <Button variant="outline" onClick={onClose} className="rounded-full">Abbrechen</Button>
        </div>
      </div>
    </Dialog>
  );
}

export function ActionDialog({ goal, kind, onClose, onDone }) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const first = goal.owner_name?.split(' ')[0] || 'die Person';
  const cfg = kind === 'signal'
    ? { title: '📈 Fortschritt gesehen!', label: 'Wo hast du Fortschritt beobachtet?',
        ph: 'Dein Pipeline-Update gestern war glasklar strukturiert.',
        help: `Ein Satz reicht — aber er muss konkret sein. ${first} soll wissen, WAS funktioniert hat, um es zu wiederholen.` }
    : { title: '💡 Idee dazu', label: `Was könnte ${first} ausprobieren?`,
        ph: 'Probier mal, die ersten 5 Minuten im Verkäufergespräch nur Fragen zu stellen. Bei mir hat das …',
        help: `Zukunft statt Vergangenheit: ‚Probier mal X‘ statt ‚Du machst immer Y‘. Eine Idee ist ein Angebot — ${first} entscheidet, ob sie passt.` };

  const submit = async () => {
    if (!text.trim()) return toast(kind === 'signal' ? 'Ein Satz reicht — was hast du gesehen?' : 'Magst du deine Idee kurz beschreiben?');
    setSaving(true);
    try {
      if (kind === 'signal') await api.teamDevGoalSignal(goal.id, text.trim());
      else await api.teamDevGoalIdea(goal.id, text.trim());
      toast.success(kind === 'signal' ? `Dein Anfeuern ist bei ${first} angekommen. 📈` : `Deine Idee liegt privat bei ${first}. 💡`);
      onDone(); onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Das hat nicht geklappt');
    } finally { setSaving(false); }
  };

  return (
    <Dialog onClose={onClose} testid={`td-${kind}-dialog`}>
      <p className="text-sm font-medium text-foreground">{cfg.title}</p>
      <p className="text-xs text-muted-foreground mt-1">Zu {goal.owner_name}s Ziel: „{goal.title}“</p>
      <div className="mt-3.5 space-y-3">
        <div>
          <p className="text-xs font-medium text-foreground mb-1.5">{cfg.label}</p>
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder={cfg.ph}
            className="text-sm bg-background rounded-md" data-testid={`td-${kind}-text`} />
          <Help>{cfg.help}</Help>
        </div>
        <div className="flex gap-2">
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full flex-1" data-testid={`td-${kind}-submit`}>
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Senden
          </Button>
          <Button variant="outline" onClick={onClose} className="rounded-full">Abbrechen</Button>
        </div>
      </div>
    </Dialog>
  );
}

function CompleteDialog({ goal, onClose, onDone }) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!note.trim()) return toast('Ein Satz für das Team: Was hat dir geholfen?');
    setSaving(true);
    try {
      await api.teamDevUpdateGoal(goal.id, { action: 'complete', closing_note: note.trim() });
      fireConfetti();
      toast.success('🎉 Geschafft! Das Team sieht jetzt auch, was dir geholfen hat — danke, dass du das teilst.', { duration: 6000 });
      onDone(); onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Das hat nicht geklappt');
    } finally { setSaving(false); }
  };
  return (
    <Dialog onClose={onClose} testid="td-complete-dialog">
      <p className="text-sm font-medium text-foreground">🎉 Ziel abschließen: „{goal.title}“</p>
      <div className="mt-3.5 space-y-3">
        <div>
          <p className="text-xs font-medium text-foreground mb-1.5">Was hat geholfen?</p>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
            placeholder="Die 3 wichtigsten Dinge, die dich weitergebracht haben …"
            className="text-sm bg-background rounded-md" data-testid="td-complete-note" />
          <Help>Steht öffentlich auf deiner archivierten Karte — so lernt das Team mit.</Help>
        </div>
        <div className="flex gap-2">
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full flex-1" data-testid="td-complete-submit">
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Abschließen
          </Button>
          <Button variant="outline" onClick={onClose} className="rounded-full">Noch nicht</Button>
        </div>
      </div>
    </Dialog>
  );
}

function GoalCard({ g, i, onAction, onObservation, reload }) {
  const [dialog, setDialog] = useState(null);
  const [showFeed, setShowFeed] = useState(false);
  const first = g.owner_name?.split(' ')[0] || '';

  const setStatus = async (action) => {
    try {
      await api.teamDevUpdateGoal(g.id, { action });
      toast.success(action === 'pause' ? 'Ziel pausiert — es wartet auf dich, wann immer du bereit bist.' : 'Ziel ist wieder aktiv. 🌱');
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Das hat nicht geklappt'); }
  };

  return (
    <div className={`td-rise td-card bg-card border rounded-lg p-5 flex flex-col ${g.is_mine ? 'border-emerald-500/30' : 'border-border'}`}
      style={rise(i)} data-testid={`td-goal-card-${i}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Avatar name={g.owner_name} />
        <p className="text-sm font-medium text-foreground">{g.owner_name}{g.is_mine && <span className="text-[9px] font-mono uppercase text-emerald-400 ml-1.5">Du</span>}</p>
        <span className={`text-[8px] font-mono uppercase border rounded-full px-2 py-0.5 ${SKILL_STYLE[g.skill_type]}`}>{SKILL_LABEL[g.skill_type]}</span>
        {g.status === 'paused' && <span className="text-[9px] font-mono uppercase border border-border rounded px-1.5 py-0.5 text-muted-foreground">pausiert</span>}
      </div>
      <p className="text-base text-foreground mt-2.5 leading-snug font-medium">{g.title}</p>
      {g.description && <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{g.description}</p>}
      {g.is_mine && g.origin_note && (
        <p className="text-[11px] text-muted-foreground/80 italic mt-2 border-l-2 border-border pl-2" data-testid={`td-goal-origin-shown-${i}`}>
          {g.origin_note} <span className="not-italic text-[9px] font-mono">(nur für dich)</span>
        </p>
      )}

      <div className="flex items-center gap-3 mt-3.5">
        <span className="text-2xl td-plant" title="Momentum wächst mit jedem Signal und jeder Idee">{g.momentum.stage}</span>
        <div className="text-[11px] text-muted-foreground leading-tight">
          <p>{g.momentum.signals_week > 0
            ? `${g.momentum.signals_week} Fortschritts-Beobachtung${g.momentum.signals_week === 1 ? '' : 'en'} diese Woche`
            : 'Momentum wächst mit jedem Signal'}</p>
          {g.trend.length > 0 && (
            <div className="flex items-center gap-1 mt-1">
              {g.trend.map((t, k) => (
                <span key={k} className={`w-2 h-2 rounded-full ${TREND_STYLE[t.status]}`} title={`${t.week_key}: ${TREND_LABEL[t.status]}`} />
              ))}
              <span className="text-[9px] font-mono ml-1">Wochen-Trend</span>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 pt-3.5 border-t border-border/60">
        {g.is_mine ? (
          <div className="space-y-2.5">
            {(g.signals?.length > 0 || g.ideas?.length > 0) && (
              <button onClick={() => setShowFeed((s) => !s)} data-testid={`td-goal-feed-toggle-${i}`}
                className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors">
                {showFeed ? 'Einklappen' : `Was das Team dir mitgibt (${(g.signals?.length || 0) + (g.ideas?.length || 0)})`}
              </button>
            )}
            {showFeed && (
              <div className="space-y-1.5 td-pop">
                {(g.signals || []).map((s) => (
                  <p key={s.id} className="text-xs text-foreground/90 bg-emerald-500/5 border border-emerald-500/15 rounded-md px-2.5 py-1.5">
                    📈 <span className="font-medium">{s.author_name}:</span> {s.note}
                  </p>
                ))}
                {(g.ideas || []).map((idn) => (
                  <p key={idn.id} className="text-xs text-foreground/90 bg-violet-500/5 border border-violet-500/15 rounded-md px-2.5 py-1.5">
                    💡 <span className="font-medium">{idn.author_name}:</span> {idn.text}
                    {idn.reaction && <span className="text-[9px] font-mono text-muted-foreground ml-1.5">({{ try: 'probierst du aus', later: 'später', no_fit: 'passt nicht' }[idn.reaction]})</span>}
                  </p>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {g.status === 'active' ? (
                <>
                  <Button size="sm" onClick={() => setDialog('complete')} className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs rounded-full" data-testid={`td-goal-complete-${i}`}>
                    <PartyPopper className="w-3 h-3 mr-1" /> Geschafft!
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setStatus('pause')} className="h-7 text-xs rounded-full" data-testid={`td-goal-pause-${i}`}>
                    <Pause className="w-3 h-3 mr-1" /> Pausieren
                  </Button>
                </>
              ) : (
                <Button size="sm" variant="outline" onClick={() => setStatus('resume')} className="h-7 text-xs rounded-full" data-testid={`td-goal-resume-${i}`}>
                  <Play className="w-3 h-3 mr-1" /> Fortsetzen
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setDialog('signal')} data-testid={`td-goal-signal-${i}`}
              className="h-7 text-xs rounded-full border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300">
              <TrendingUp className="w-3 h-3 mr-1" /> Fortschritt gesehen!
            </Button>
            <Button size="sm" variant="outline" onClick={() => setDialog('idea')} data-testid={`td-goal-idea-${i}`}
              className="h-7 text-xs rounded-full border-violet-500/30 text-violet-400 hover:bg-violet-500/10 hover:text-violet-300">
              <Lightbulb className="w-3 h-3 mr-1" /> Idee dazu
            </Button>
            <Button size="sm" variant="outline" onClick={() => onObservation(g)} data-testid={`td-goal-observe-${i}`}
              title={`Für alles, was kein Fortschritt ist: Hier kannst du in Ruhe und mit Kontext beschreiben, was dir aufgefallen ist. Geht privat an ${first}.`}
              className="h-7 text-xs rounded-full">
              <Eye className="w-3 h-3 mr-1" /> Beobachtung
            </Button>
          </div>
        )}
      </div>

      {dialog === 'signal' && <ActionDialog goal={g} kind="signal" onClose={() => setDialog(null)} onDone={reload} />}
      {dialog === 'idea' && <ActionDialog goal={g} kind="idea" onClose={() => setDialog(null)} onDone={reload} />}
      {dialog === 'complete' && <CompleteDialog goal={g} onClose={() => setDialog(null)} onDone={reload} />}
    </div>
  );
}

export default function GoalsTab({ meId, gotoObservation }) {
  const [data, setData] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    api.teamDevGoals().then((res) => setData(res.data)).catch(() => toast.error('Ziele konnten nicht geladen werden'));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;

  const tryCreate = () => {
    if (data.my_active_count >= 2) {
      return toast('Zwei aktive Ziele sind das Maximum — Fokus schlägt Menge. Schließe erst eines ab oder pausiere es.');
    }
    setCreating(true);
  };

  return (
    <div className="space-y-6" data-testid="td-goals">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <p className="text-sm font-medium text-foreground">Zielkarten</p>
          <p className="text-xs text-muted-foreground mt-0.5">Jede Karte ist eine Ambition — feuere an, gib Ideen, teile Beobachtungen.</p>
        </div>
        <Button onClick={tryCreate} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full" data-testid="td-goal-create-btn">
          <Plus className="w-4 h-4 mr-1" /> Ziel anlegen
        </Button>
      </div>

      {data.active.length === 0 ? (
        <div className="bg-card border border-dashed border-border rounded-lg p-10 text-center td-rise" data-testid="td-goals-empty">
          <p className="text-2xl mb-2">🌱</p>
          <p className="text-sm text-foreground">Noch keine Zielkarten.</p>
          <p className="text-xs text-muted-foreground mt-1">Was willst du können? Wie willst du wirken? Leg den Anfang — das Team feuert dich an.</p>
          <button onClick={tryCreate} data-testid="td-goals-empty-cta"
            className="mt-3 text-xs font-medium text-emerald-400 border border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/15 rounded-full px-4 py-1.5 transition-colors">
            Mein erstes Ziel anlegen
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {data.active.map((g, i) => (
            <GoalCard key={g.id} g={g} i={i} reload={load} onObservation={gotoObservation} />
          ))}
        </div>
      )}

      {data.archived.length > 0 && (
        <div className="space-y-2.5">
          <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">🎉 Abgeschlossene Ziele — das Team lernt mit</p>
          <div className="grid md:grid-cols-2 gap-3">
            {data.archived.map((g, i) => (
              <div key={g.id} className="bg-card border border-border rounded-lg p-4 opacity-90" data-testid={`td-goal-archived-${i}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <Avatar name={g.owner_name} className="w-5 h-5 text-[9px]" />
                  <p className="text-xs font-medium text-foreground">{g.owner_name}</p>
                  <span className={`text-[8px] font-mono uppercase border rounded-full px-1.5 py-px ${SKILL_STYLE[g.skill_type]}`}>{SKILL_LABEL[g.skill_type]}</span>
                  <span className="text-[9px] text-muted-foreground font-mono ml-auto">{fmtDate(g.closed_at)}</span>
                </div>
                <p className="text-sm text-foreground mt-1.5">{g.title}</p>
                {g.closing_note && (
                  <p className="text-xs text-emerald-400/90 mt-1.5">💚 Was geholfen hat: <span className="text-foreground/80">{g.closing_note}</span></p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {creating && <CreateGoalDialog onClose={() => setCreating(false)} onCreated={load} />}
    </div>
  );
}
